import React, { useEffect, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

export default function Managers() {
  const [managers,      setManagers]      = useState([]);
  const [employees,     setEmployees]     = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showAdd,       setShowAdd]       = useState(false);
  const [selectedMgr,   setSelectedMgr]   = useState(null); // drill-down view
  const [showAssign,    setShowAssign]     = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [search,        setSearch]        = useState('');
  const [empSearch,     setEmpSearch]     = useState('');
  const [selectedEmps,  setSelectedEmps]  = useState([]);
  const [form, setForm] = useState({
    employee_code:'', name:'', email:'', password:'', department:'', joining_date:''
  });

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/managers'), api.get('/employees')])
      .then(([m, e]) => {
        setManagers(m.data.managers);
        setEmployees(e.data.employees);
        // Keep selectedMgr in sync after reload
        if (selectedMgr) {
          const updated = m.data.managers.find(mg => mg.id === selectedMgr.id);
          if (updated) setSelectedMgr(updated);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const getTeam    = (mgrId) => employees.filter(e => e.manager_id === mgrId && e.is_active);
  const getAvail   = (mgrId) => employees.filter(e => e.is_active && e.manager_id !== mgrId);
  const filtered   = managers.filter(m => !search ||
    m.name.toLowerCase().includes(search.toLowerCase()) ||
    m.email.toLowerCase().includes(search.toLowerCase()));

  // ── Create manager ────────────────────────────────────────────────────────
  const handleCreate = async (ev) => {
    ev.preventDefault(); setSaving(true);
    try {
      await api.post('/managers/create', form);
      toast.success('Manager created!');
      setShowAdd(false);
      setForm({ employee_code:'', name:'', email:'', password:'', department:'', joining_date:'' });
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  // ── Assign employees ──────────────────────────────────────────────────────
  const handleAssign = async () => {
    if (!selectedEmps.length) { toast.error('Select at least one employee.'); return; }
    setSaving(true);
    try {
      await Promise.all(selectedEmps.map(id => api.put(`/employees/${id}`, { manager_id: selectedMgr.id })));
      toast.success(`${selectedEmps.length} employee(s) assigned!`);
      setShowAssign(false);
      setSelectedEmps([]);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  // ── Remove employee from manager ──────────────────────────────────────────
  const handleRemove = async (emp) => {
    if (!window.confirm(`Remove ${emp.name} from ${selectedMgr.name}'s team?`)) return;
    try {
      await api.put(`/employees/${emp.id}`, { manager_id: null });
      toast.success(`${emp.name} removed.`);
      load();
    } catch { toast.error('Failed.'); }
  };

  // ── Deactivate manager ────────────────────────────────────────────────────
  const handleDeactivate = async (mgr) => {
    if (!window.confirm(`Deactivate ${mgr.name}?`)) return;
    try {
      await api.patch(`/employees/${mgr.id}/toggle-status`);
      toast.success('Manager deactivated.');
      setSelectedMgr(null);
      load();
    } catch { toast.error('Failed.'); }
  };

  const toggleEmp = (id) =>
    setSelectedEmps(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]);

  // ──────────────────────────────────────────────────────────────────────────
  // DRILL-DOWN VIEW — selected manager's team
  // ──────────────────────────────────────────────────────────────────────────
  if (selectedMgr) {
    const team = getTeam(selectedMgr.id);
    const filteredTeam = team.filter(e =>
      !empSearch || e.name.toLowerCase().includes(empSearch.toLowerCase()) ||
      e.email.toLowerCase().includes(empSearch.toLowerCase())
    );

    return (
      <div>
        {/* Back button */}
        <button
          className="btn btn-secondary btn-sm"
          style={{ marginBottom:20 }}
          onClick={() => { setSelectedMgr(null); setEmpSearch(''); setShowAssign(false); setSelectedEmps([]); }}
        >
          ← Back to Managers
        </button>

        {/* Manager profile card */}
        <div className="card" style={{ marginBottom:20, padding:'20px 24px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
            <div className="avatar" style={{ width:52, height:52, fontSize:20, flexShrink:0 }}>
              {selectedMgr.name.charAt(0)}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                <span style={{ fontWeight:700, fontSize:17 }}>{selectedMgr.name}</span>
                {selectedMgr.employee_code && (
                  <span style={{ fontSize:12, color:'#6c63ff', background:'rgba(108,99,255,.12)', padding:'2px 8px', borderRadius:20, fontWeight:600 }}>
                    #{selectedMgr.employee_code}
                  </span>
                )}
                <span className="badge badge-blue">Manager</span>
              </div>
              <div style={{ color:'#6b7280', fontSize:13, marginTop:3 }}>{selectedMgr.email}</div>
              <div style={{ display:'flex', gap:14, marginTop:6, flexWrap:'wrap' }}>
                {selectedMgr.department && <span className="text-sm text-muted">🏢 {selectedMgr.department}</span>}
                {selectedMgr.joining_date && <span className="text-sm text-muted">📅 {format(new Date(selectedMgr.joining_date),'MMM d, yyyy')}</span>}
                <span className="text-sm text-muted">👥 {team.length} team member{team.length!==1?'s':''}</span>
              </div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-primary" onClick={() => { setShowAssign(true); setSelectedEmps([]); }}>
                + Add Employees
              </button>
              <button className="btn btn-danger btn-sm" onClick={() => handleDeactivate(selectedMgr)}>
                Deactivate
              </button>
            </div>
          </div>
        </div>

        {/* Team header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:10 }}>
          <h2 style={{ fontSize:16, fontWeight:600 }}>
            Team Members <span style={{ color:'#6b7280', fontWeight:400, fontSize:14 }}>({team.length})</span>
          </h2>
          <input
            className="search-box"
            placeholder="Search team…"
            value={empSearch}
            onChange={e => setEmpSearch(e.target.value)}
            style={{ maxWidth:260 }}
          />
        </div>

        {filteredTeam.length === 0 ? (
          <div className="card">
            <div className="empty">
              <div className="empty-icon">👥</div>
              <p>{team.length === 0 ? 'No employees assigned yet.' : 'No results found.'}</p>
              {team.length === 0 && (
                <button className="btn btn-primary" style={{ marginTop:12 }} onClick={() => { setShowAssign(true); setSelectedEmps([]); }}>
                  + Add Employees
                </button>
              )}
            </div>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {filteredTeam.map(emp => (
              <div key={emp.id} className="card" style={{ padding:'14px 20px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                  <div className="avatar" style={{ width:38, height:38, fontSize:14, flexShrink:0 }}>
                    {emp.name.charAt(0)}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                      <span style={{ fontWeight:600, fontSize:14 }}>{emp.name}</span>
                      {emp.employee_code && (
                        <span style={{ fontSize:11.5, color:'#6c63ff', background:'rgba(108,99,255,.12)', padding:'2px 7px', borderRadius:20, fontWeight:600 }}>
                          #{emp.employee_code}
                        </span>
                      )}
                      <span className="badge badge-green">Active</span>
                    </div>
                    <div style={{ color:'#6b7280', fontSize:12.5, marginTop:2 }}>{emp.email}</div>
                    <div style={{ display:'flex', gap:14, marginTop:4, flexWrap:'wrap' }}>
                      {emp.department && <span className="text-sm text-muted">🏢 {emp.department}</span>}
                      {emp.joining_date && <span className="text-sm text-muted">📅 {format(new Date(emp.joining_date),'MMM d, yyyy')}</span>}
                    </div>
                    {emp.projects?.length > 0 && (
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:8 }}>
                        {emp.projects.map(p => (
                          <span key={p.id} className="chip">📁 {p.name}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button className="btn btn-danger btn-sm" onClick={() => handleRemove(emp)}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Assign employees modal */}
        {showAssign && (
          <div className="overlay" onClick={() => setShowAssign(false)}>
            <div className="modal" style={{ maxWidth:540 }} onClick={e => e.stopPropagation()}>
              <div className="modal-title">Add Employees to {selectedMgr.name}'s Team</div>

              <div style={{ fontSize:12.5, color:'#6b7280', marginBottom:10 }}>
                Select employees to assign. Employees currently in another team will be moved.
              </div>

              <div style={{ maxHeight:360, overflowY:'auto', display:'flex', flexDirection:'column', gap:6 }}>
                {getAvail(selectedMgr.id).length === 0 ? (
                  <div style={{ textAlign:'center', color:'#6b7280', padding:'24px 0', fontSize:13 }}>
                    No available employees.
                  </div>
                ) : getAvail(selectedMgr.id).map(emp => {
                  const checked   = selectedEmps.includes(emp.id);
                  const curMgr    = managers.find(m => m.id === emp.manager_id);
                  return (
                    <div
                      key={emp.id}
                      onClick={() => toggleEmp(emp.id)}
                      style={{
                        display:'flex', alignItems:'center', gap:12, padding:'10px 14px',
                        background: checked ? 'rgba(108,99,255,.12)' : '#0f1117',
                        border:`1px solid ${checked ? '#6c63ff' : '#2a2d3a'}`,
                        borderRadius:10, cursor:'pointer', transition:'all .15s',
                      }}
                    >
                      <div style={{
                        width:18, height:18, borderRadius:5,
                        border:`2px solid ${checked ? '#6c63ff' : '#4b5563'}`,
                        background: checked ? '#6c63ff' : 'transparent',
                        display:'flex', alignItems:'center', justifyContent:'center',
                        flexShrink:0, fontSize:11, color:'#fff',
                      }}>
                        {checked && '✓'}
                      </div>
                      <div className="avatar" style={{ width:30, height:30, fontSize:11, flexShrink:0 }}>
                        {emp.name.charAt(0)}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13.5, fontWeight:600, color: checked ? '#e5e7eb' : '#d1d5db' }}>
                          {emp.name}
                          {emp.employee_code && <span style={{ fontSize:11, color:'#6c63ff', marginLeft:6 }}>#{emp.employee_code}</span>}
                        </div>
                        <div style={{ fontSize:11.5, color:'#6b7280' }}>
                          {emp.email}{emp.department && ` · ${emp.department}`}
                        </div>
                      </div>
                      <span style={{
                        fontSize:11, padding:'2px 8px', borderRadius:20, flexShrink:0,
                        color: curMgr ? '#f59e0b' : '#6b7280',
                        background: curMgr ? 'rgba(245,158,11,.1)' : '#22263a',
                        border:`1px solid ${curMgr ? 'rgba(245,158,11,.2)' : '#2a2d3a'}`,
                      }}>
                        {curMgr ? `→ ${curMgr.name}` : 'Unassigned'}
                      </span>
                    </div>
                  );
                })}
              </div>

              {selectedEmps.length > 0 && (
                <div style={{ marginTop:10, padding:'7px 12px', background:'rgba(108,99,255,.08)', borderRadius:8, fontSize:13, color:'#6c63ff' }}>
                  {selectedEmps.length} employee(s) selected
                </div>
              )}

              <div className="modal-footer" style={{ marginTop:16 }}>
                <button className="btn btn-secondary" onClick={() => { setShowAssign(false); setSelectedEmps([]); }}>Cancel</button>
                <button className="btn btn-primary" disabled={saving || !selectedEmps.length} onClick={handleAssign}>
                  {saving ? 'Assigning…' : `Assign ${selectedEmps.length || ''} Employee(s)`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // MANAGERS LIST VIEW
  // ──────────────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Managers</h1>
          <p className="page-sub">{managers.filter(m=>m.is_active).length} active managers</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ New Manager</button>
      </div>

      <input
        className="search-box"
        placeholder="Search by name or email…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom:16 }}
      />

      {loading ? (
        <div className="loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="card"><div className="empty"><div className="empty-icon">👤</div><p>No managers found.</p></div></div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {filtered.map(mgr => {
            const team = getTeam(mgr.id);
            return (
              <div
                key={mgr.id}
                className="card"
                style={{ padding:'16px 22px', cursor:'pointer', transition:'border-color .15s' }}
                onClick={() => setSelectedMgr(mgr)}
                onMouseEnter={e => e.currentTarget.style.borderColor='#6c63ff'}
                onMouseLeave={e => e.currentTarget.style.borderColor='#2a2d3a'}
              >
                <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                  <div className="avatar" style={{ width:44, height:44, fontSize:16, flexShrink:0 }}>
                    {mgr.name.charAt(0)}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                      <span style={{ fontWeight:600, fontSize:15, color:'#fff' }}>{mgr.name}</span>
                      {mgr.employee_code && (
                        <span style={{ fontSize:11.5, color:'#6c63ff', background:'rgba(108,99,255,.12)', padding:'2px 8px', borderRadius:20, fontWeight:600 }}>
                          #{mgr.employee_code}
                        </span>
                      )}
                      <span className={`badge ${mgr.is_active ? 'badge-blue' : 'badge-red'}`}>
                        {mgr.is_active ? 'Manager' : 'Inactive'}
                      </span>
                    </div>
                    <div style={{ color:'#6b7280', fontSize:12.5, marginTop:2 }}>{mgr.email}</div>
                    <div style={{ display:'flex', gap:14, marginTop:5, flexWrap:'wrap' }}>
                      {mgr.department && <span className="text-sm text-muted">🏢 {mgr.department}</span>}
                      <span className="text-sm text-muted">👥 {team.length} member{team.length!==1?'s':''}</span>
                    </div>
                    {/* Team member avatars preview */}
                    {team.length > 0 && (
                      <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
                        {team.slice(0,6).map(emp => (
                          <div key={emp.id} style={{ display:'flex', alignItems:'center', gap:5, background:'#22263a', border:'1px solid #2a2d3a', borderRadius:20, padding:'3px 10px 3px 6px' }}>
                            <div className="avatar" style={{ width:20, height:20, fontSize:9 }}>{emp.name.charAt(0)}</div>
                            <span style={{ fontSize:12, color:'#d1d5db' }}>{emp.name}</span>
                          </div>
                        ))}
                        {team.length > 6 && (
                          <span style={{ fontSize:12, color:'#6b7280', padding:'3px 10px', background:'#22263a', borderRadius:20, border:'1px solid #2a2d3a' }}>
                            +{team.length - 6} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ color:'#6b7280', fontSize:18, flexShrink:0 }}>›</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Manager Modal */}
      {showAdd && (
        <div className="overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Create New Manager</div>
            <form onSubmit={handleCreate} className="form-grid">
              <div className="field">
                <label>Employee ID * <span style={{ fontSize:11, color:'#6b7280', fontWeight:400 }}>(e.g. MGR001)</span></label>
                <input required value={form.employee_code} onChange={e=>setForm({...form,employee_code:e.target.value.trim()})} placeholder="MGR001" style={{ fontFamily:'monospace', fontWeight:600, letterSpacing:1 }}/>
              </div>
              <div className="form-row">
                <div className="field">
                  <label>Full Name *</label>
                  <input required value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Arjun Mehta"/>
                </div>
                <div className="field">
                  <label>Email *</label>
                  <input type="email" required value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="arjun@company.com"/>
                </div>
              </div>
              <div className="form-row">
                <div className="field">
                  <label>Password *</label>
                  <input type="password" required minLength={6} value={form.password} onChange={e=>setForm({...form,password:e.target.value})} placeholder="Min 6 characters"/>
                </div>
                <div className="field">
                  <label>Department</label>
                  <input value={form.department} onChange={e=>setForm({...form,department:e.target.value})} placeholder="Engineering"/>
                </div>
              </div>
              <div className="field">
                <label>Joining Date</label>
                <input type="date" value={form.joining_date} onChange={e=>setForm({...form,joining_date:e.target.value})}/>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Creating…' : 'Create Manager'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}